import { effect, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { SeasonService } from './season.service';
import { SeasonSummary } from '../../../shared/models/season';

const STORAGE_KEY = 'ootd_seen_recaps_by_user';

@Injectable({
  providedIn: 'root',
})
export class SeasonRecapService {
  readonly #authService = inject(AuthenticationService);
  readonly #seasonService = inject(SeasonService);
  readonly #router = inject(Router);

  readonly isOpen = signal<boolean>(false);
  readonly activeSeasonId = signal<string | null>(null);

  readonly recapResource = this.#seasonService.getSeasonRecapResource(() => this.activeSeasonId());

  private readonly navigationEnd = toSignal(
    this.#router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects)
    )
  );

  private transitionTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private transitionIntervalId: ReturnType<typeof setInterval> | null = null;
  private scheduledSeasonId: string | null = null;

  constructor() {
    effect(() => {
      const seasons = this.#seasonService.accessibleSeasonsResource.value();
      if (!seasons || seasons.length === 0) {
        return;
      }

      const currentSeason = seasons.find(s => s.status === 'CURRENT');
      if (currentSeason) {
        this.scheduleTransitionCheck(currentSeason);
      }
    });

    effect(() => {
      const user = this.#authService.authenticatedUser();
      const seasons = this.#seasonService.accessibleSeasonsResource.value();

      if (!user || !user.onboarded || !seasons || seasons.length === 0 || this.isOpen()) {
        return;
      }

      if (this.isInOnboardingOrAuth()) {
        return;
      }

      const pastSeasons = seasons.filter(s => s.status === 'PAST');
      if (pastSeasons.length === 0) {
        return;
      }

      const latestPastSeason = pastSeasons[pastSeasons.length - 1];
      if (!this.isRecapSeenForUser(user.id, latestPastSeason.id)) {
        if (this.activeSeasonId() !== latestPastSeason.id) {
          this.activeSeasonId.set(latestPastSeason.id);
        }
      }
    });

    effect(() => {
      const user = this.#authService.authenticatedUser();
      const recap = this.recapResource.value();
      const seasonId = this.activeSeasonId();

      if (!user || !user.onboarded || !recap || !seasonId || this.isOpen()) {
        return;
      }

      if (this.isInOnboardingOrAuth()) {
        return;
      }

      if (recap.season.id === seasonId && !this.isRecapSeenForUser(user.id, seasonId)) {
        if (recap.podium.length > 0 || recap.stats.totalVotes > 0) {
          this.isOpen.set(true);
        } else {
          this.markRecapSeenForUser(user.id, seasonId);
        }
      }
    });
  }

  private scheduleTransitionCheck(currentSeason: SeasonSummary): void {
    if (this.scheduledSeasonId === currentSeason.id && (this.transitionTimeoutId || this.transitionIntervalId)) {
      return;
    }

    this.clearScheduledTransition();
    this.scheduledSeasonId = currentSeason.id;
    const endStr = currentSeason.endDateTime;
    const endDate = endStr.endsWith('Z') || endStr.includes('+') ? new Date(endStr) : new Date(`${endStr}Z`);
    const delay = endDate.getTime() - Date.now();

    if (delay <= 0) {
      this.startTransitionPolling(currentSeason.id);
    } else {
      this.transitionTimeoutId = setTimeout(() => {
        this.startTransitionPolling(currentSeason.id);
      }, delay);
    }
  }

  private startTransitionPolling(oldSeasonId: string): void {
    if (this.transitionIntervalId) {
      return;
    }

    this.#seasonService.accessibleSeasonsResource.reload?.();

    this.transitionIntervalId = setInterval(() => {
      this.#seasonService.accessibleSeasonsResource.reload?.();
      const seasons = this.#seasonService.accessibleSeasonsResource.value();
      const currentSeason = seasons?.find(s => s.status === 'CURRENT');

      if (!currentSeason || currentSeason.id !== oldSeasonId) {
        this.clearScheduledTransition();
      }
    }, 2500);
  }

  private clearScheduledTransition(): void {
    if (this.transitionTimeoutId) {
      clearTimeout(this.transitionTimeoutId);
      this.transitionTimeoutId = null;
    }
    if (this.transitionIntervalId) {
      clearInterval(this.transitionIntervalId);
      this.transitionIntervalId = null;
    }
  }

  private isInOnboardingOrAuth(): boolean {
    const url = this.navigationEnd() ?? this.#router.url ?? '';
    return url.startsWith('/onboarding') || url.startsWith('/login');
  }

  openRecap(seasonId: string) {
    this.activeSeasonId.set(seasonId);
    this.isOpen.set(true);
  }

  closeRecap() {
    const seasonId = this.activeSeasonId();
    const userId = this.#authService.authenticatedUserId();
    if (seasonId && userId) {
      this.markRecapSeenForUser(userId, seasonId);
    }
    this.isOpen.set(false);
  }

  isRecapSeenForUser(userId: string, seasonId: string): boolean {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      const parsed: Record<string, string[]> = JSON.parse(raw);
      return Array.isArray(parsed[userId]) && parsed[userId].includes(seasonId);
    } catch {
      return false;
    }
  }

  markRecapSeenForUser(userId: string, seasonId: string): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed: Record<string, string[]> = raw ? JSON.parse(raw) : {};
      const userList = Array.isArray(parsed[userId]) ? parsed[userId] : [];
      if (!userList.includes(seasonId)) {
        userList.push(seasonId);
      }
      parsed[userId] = userList;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      // Ignored
    }
  }
}
